import React, { useState, useEffect, useRef } from 'react';
import { Brain, Zap, Server, Layers, Code, ArrowRight } from 'lucide-react';
import { portfolioData } from '../data/mock';

const iconMap = {
  brain: Brain,
  zap: Zap,
  server: Server,
  layers: Layers,
  code: Code
};

const PersonalBrand = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="brand" ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-6">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-4">
              <span>Engineering Identity</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
              Not Just a <span className="gradient-text">Developer</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <p className="text-center text-gray-400 text-lg leading-relaxed">{portfolioData.brandDescription}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-16">
            {portfolioData.brandIntersection.map((item, i) => {
              const Icon = iconMap[item.icon] || Code;
              return (
                <div key={item.name} className="group glass rounded-2xl p-6 text-center hover:border-indigo-500/20 transition-all duration-500 hover:-translate-y-1"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: `${i * 100}ms`,
                    transitionProperty: 'all',
                    transitionDuration: '0.6s',
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                  }}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="text-indigo-400" size={24} />
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-2">{item.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>

          <div className="glass rounded-3xl p-8 sm:p-12 border border-white/5 max-w-4xl mx-auto">
            <div className="flex items-center space-x-4 mb-6">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />
              <span className="text-indigo-400 text-xs font-mono whitespace-nowrap">Full-Stack Engineer</span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />
            </div>
            <p className="text-gray-400 text-center leading-relaxed">
              From <span className="text-white">Linux servers and HPC clusters</span> to{' '}
              <span className="text-white">APIs and backend systems</span> to{' '}
              <span className="text-white">AI agents and intelligent automation workflows</span>
              — I build across the entire stack.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalBrand;
